import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Modal, Tab, TabGroup, TabPanel, TogglePillOption } from '@base-app';

import { MOCK_BASIC_BAR_CHART, STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { Sizes } from '@/common/types';

import { InfoTitle, TabContextProvider } from '@/common/components';
import { PartnerOrganization } from '@/modules/core/types';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';

import { ChartsPanelWithMapProps, TabId } from './ChartsPanelWithMap.types';
import ChartsPanel from '../../../shared/ChartsPanel/ChartsPanel';
import ModalParticipants from '../../../shared/ModalParticipants/ModalParticipants';
import ChartPillAndDateFilter from '../../../shared/ChartPillAndDateFilter/ChartPillAndDateFilter';
import ChartContainer from '../../../shared/ChartContainer/ChartContainer';
import ParticipantDensityMap from '../ParticipantDensityMap/ParticipantDensityMap';
import { PillIds } from '../../../shared/types/PillIds';
import SectionTitle from '../../../shared/SectionTitle/SectionTitle';

const ChartsPanelWithMap: FC<ChartsPanelWithMapProps> = ({
  isLoading = false,
  chartData,
  onCloseModal,
  // This prop should be removed if the
  // current button to expand the graph
  // if the change date button from the page is
  // independent from the button on the modal
  // currently the expand graph is disabled
  // if the button is enable. Do not change the dates
  // from useLayoutThree as it will change dates
  // on the page as well. Make a new call in here
  onSelectDateRange,
  selectedSection,
  sectionTitle,
  selectedDateRange,
  showMapSection = false,
  showModal,
  goalLine,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [tooltipText, setTooltipText] = useState(
    t('participant_recruitment.chart_tooltip', {
      section: PillIds.CONSENTED.charAt(0).toUpperCase() + PillIds.CONSENTED.slice(1),
    }),
  );
  const [, setTitle] = useState<string>('');
  const [mapTitle, setMapTitle] = useState<string>('');
  const [selectedPill, setSelectedPill] = useState<string>(PillIds.CONSENTED);
  const organizationName = useOrganizationName();
  const isMemorialHermann =
    organizationName === PartnerOrganization.MEMORIAL_HERMANN ||
    organizationName === PartnerOrganization.HELIX_MEMORIAL_HERMANN;

  const interestedSectionTitle = t(
    `participant_recruitment.${isMemorialHermann ? 'available_for_consent' : 'interested'}.title`,
  );

  const consentedPillOptions: TogglePillOption[] = [
    {
      id: PillIds.CONSENTED,
      label: t('participant_recruitment.consented.title'),
    },
    {
      id: PillIds.RATE_CONSENTED,
      label: t('participant_recruitment.conversion_rate'),
    },
  ];

  const interestedPillOptions: TogglePillOption[] = [
    {
      id: PillIds.INTERESTED,
      label: interestedSectionTitle,
    },
    {
      id: PillIds.RATE_INTERESTED,
      label: t('participant_recruitment.conversion_rate'),
    },
  ];

  useEffect(() => {
    if (selectedPill) {
      selectedPill === PillIds.INTERESTED
        ? setMapTitle(
            t(`participant_recruitment.${isMemorialHermann ? 'available_for_consent' : selectedPill}.map_title`),
          )
        : setMapTitle(t(`participant_recruitment.${selectedPill}.map_title`));
    }
  }, [selectedPill]);

  useEffect(() => {
    setTitle(
      selectedSection === PillIds.CONSENTED ? t('participant_recruitment.consented.title') : interestedSectionTitle,
    );

    if (selectedSection === PillIds.CONSENTED) {
      switch (selectedPill) {
        case PillIds.CONSENTED:
          setTooltipText(
            t('participant_recruitment.tooltip_number_of', {
              section: t('participant_recruitment.consented.title'),
            }),
          );
          break;
        case PillIds.RATE_CONSENTED:
          setTooltipText(
            t('participant_recruitment.tooltip_percentage_of', {
              section: t('participant_recruitment.consented.title'),
            }),
          );
          break;
      }
    }

    if (selectedSection === PillIds.INTERESTED) {
      switch (selectedPill) {
        case PillIds.INTERESTED:
          setTooltipText(
            t('participant_recruitment.tooltip_number_of', {
              section: interestedSectionTitle,
            }),
          );
          break;
        case PillIds.RATE_INTERESTED:
          setTooltipText(
            t('participant_recruitment.tooltip_percentage_of', {
              section: interestedSectionTitle,
            }),
          );
          break;
      }
    }
  }, [selectedSection, selectedPill]);

  const handlePillClick = (id: string): void => {
    setSelectedPill(id);
  };

  const tabStyles = { padding: '$2 $8', fontWeight: '$bold' };

  return (
    <>
      {!isLoading && (
        <ChartsPanel
          chartTooltip={tooltipText}
          data-testid={`${selectedSection}-charts-panel`}
          chartData={chartData}
          handleSelectedPill={handlePillClick}
          pillOptions={selectedSection === PillIds.CONSENTED ? consentedPillOptions : interestedPillOptions}
          selectedDateRange={selectedDateRange}
          selectedPill={selectedSection}
          showMapSection={showMapSection}
          goalLine={goalLine}
        />
      )}
      <TabContextProvider value={TabId.GRAPH_VIEW}>
        <Modal
          title={
            <>
              <SectionTitle label={sectionTitle} />
              <TabGroup css={{ borderBottom: 'none' }} value={TabId.GRAPH_VIEW}>
                <Tab css={tabStyles} label={t('participant_recruitment.graph_view')} value={TabId.GRAPH_VIEW} />
                <Tab css={tabStyles} label={t('participant_recruitment.map_view')} value={TabId.MAP_VIEW} />
              </TabGroup>
            </>
          }
          show={showModal}
          onClose={onCloseModal}
          size={Sizes.XXX_LARGE}
        >
          <TabPanel value={TabId.GRAPH_VIEW}>
            <ModalParticipants
              chartData={MOCK_BASIC_BAR_CHART.series}
              onChangeDate={onSelectDateRange}
              pillOptions={selectedSection === PillIds.CONSENTED ? consentedPillOptions : interestedPillOptions}
              selectedPill={selectedSection === PillIds.CONSENTED ? PillIds.CONSENTED : PillIds.INTERESTED}
            />
          </TabPanel>
          <TabPanel value={TabId.MAP_VIEW}>
            <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <ChartPillAndDateFilter
                pillOptions={selectedSection === PillIds.CONSENTED ? consentedPillOptions : interestedPillOptions}
                selectedPill={PillIds.CONSENTED}
                onChangeDate={onSelectDateRange}
                onChangePill={handlePillClick}
              />
              <ChartContainer css={{ padding: '$0' }}>
                <Box>
                  <InfoTitle
                    css={{
                      marginBottom: '$0',
                      padding: '$6 $0 $6 $10',
                    }}
                    testId="density-map-title"
                  >
                    {mapTitle}
                  </InfoTitle>
                  <ParticipantDensityMap tooltipText={tooltipText} />
                </Box>
              </ChartContainer>
            </Box>
          </TabPanel>
        </Modal>
      </TabContextProvider>
    </>
  );
};

export default ChartsPanelWithMap;
